import { z } from 'zod';
import type { FeatureArea } from '@trinity/features-registry';
import {
  additionalAgentContext,
  applicationTooltip,
  autoTagFiles,
  availableWidgets,
  browserTabName,
  buildFlatFeaturesModel,
  chatAggregationVisualization,
  chatAtMentionFilter,
  chatAuraIframesEnabled,
  chatContextSelector,
  chatExpirationHours,
  chatFileUploads,
  chatGuidelines,
  chatIconVisibility,
  chatInputAttachmentsEnabled,
  chatInputAttachmentAllowedMimeTypes,
  chatInteractionAvailableModes,
  chatInteractionDefaultMode,
  citationsEnabled,
  chatLiveAvatar,
  chatLiveAvatarId,
  chatLiveAvatarTtsConfig,
  chatReadAloud,
  chatReadAloudTtsConfig,
  chatVoiceInput,
  chatWelcomeScreen,
  chatWelcomeScreenContent,
  complianceLayer,
  copyChatMessages,
  deleteChatsOnSessionEnd,
  disableChatInputWithLandingCards,
  documentIconVisibility,
  engagementFilter,
  engagementResponseLabels,
  exportChatData,
  faqChatContextIdentifier,
  faqIcon,
  FEATURES,
  FeatureAreaSchema,
  FeatureType,
  followUps,
  formWorkflow,
  formWorkflows,
  hcpAlertsTool,
  hcpClinicalTrials,
  hcpConferencePresence,
  hcpContactInformation,
  hcpInteractionsSummary,
  hcpKeyDiscussionQuestions,
  hcpKeyInsights,
  hcpLeaderTopics,
  hcpOpensearchIndex,
  hcpProfiles,
  hcpPublications,
  hcpSchedulingAssistant,
  homeButton,
  homeButtonTopHeader,
  homeScreen,
  identificationPrompt,
  inputGuardrailsPrompt,
  isJsonDef,
  keyDiscussionQuestionsDisclaimer,
  maxUserChatQueries,
  predictiveScheduling,
  repAutocompleteEndpoint,
  repSearchEndpoint,
  repTwoMinuteSummaryTool,
  shareChats,
  smartRouting,
  smartRoutingEnabled,
  speechServicesProvider,
  supportIcon,
  useNewExperience,
  uploadFiles,
  viewHcpProfileTool,
  voiceMode,
  workspaceBrands,
  workspaces,
} from '@trinity/features-registry';

export { FeatureAreaSchema, FeatureType };
export type { FeatureArea };

const BooleanFeature = z.object({
  type: z.literal(FeatureType.Boolean),
  value: z.boolean(),
});
export type BooleanFeature = z.infer<typeof BooleanFeature>;

const StringFeature = z.object({
  type: z.literal(FeatureType.String),
  value: z.string(),
});
export type StringFeature = z.infer<typeof StringFeature>;

const NumberFeature = z.object({
  type: z.literal(FeatureType.Number),
  value: z.number(),
});
export type NumberFeature = z.infer<typeof NumberFeature>;

const MarkdownFeature = z.object({
  type: z.literal(FeatureType.Markdown),
  value: z.string(),
});
export type MarkdownFeature = z.infer<typeof MarkdownFeature>;

const MultiSelectFeature = z.object({
  type: z.literal(FeatureType.MultiSelect),
  value: z.array(z.string()),
});
export type MultiSelectFeature = z.infer<typeof MultiSelectFeature>;

const JsonFeature = z.object({
  type: z.literal(FeatureType.Json),
  value: z.unknown(),
});
export type JsonFeature = z.infer<typeof JsonFeature>;

const StringListFeature = z.object({
  type: z.literal(FeatureType.StringList),
  value: z.array(z.string()),
});
export type StringListFeature = z.infer<typeof StringListFeature>;

export const FeatureValueSchema = z.discriminatedUnion('type', [
  BooleanFeature,
  StringFeature,
  NumberFeature,
  MarkdownFeature,
  MultiSelectFeature,
  JsonFeature,
  StringListFeature,
]);
export type FeatureValue = z.infer<typeof FeatureValueSchema>;

const BlankValidation = <FT extends FeatureType>(ft: FT) => {
  return z.object({
    type: z.literal(ft),
  });
};

const BooleanValidation = BlankValidation(FeatureType.Boolean);
export type BooleanValidation = z.infer<typeof BooleanValidation>;

const StringValidation = z.object({
  type: z.literal(FeatureType.String),
  validValues: z.array(z.string()).optional(),
});
export type StringValidation = z.infer<typeof StringValidation>;

const NumberValidation = BlankValidation(FeatureType.Number);
export type NumberValidation = z.infer<typeof NumberValidation>;

const MarkdownValidation = BlankValidation(FeatureType.Markdown);
export type MarkdownValidation = z.infer<typeof MarkdownValidation>;

const MultiSelectValidation = z.object({
  type: z.literal(FeatureType.MultiSelect),
  validValues: z.array(z.string()),
});
export type MultiSelectValidation = z.infer<typeof MultiSelectValidation>;

const JsonValidation = BlankValidation(FeatureType.Json);
export type JsonValidation = z.infer<typeof JsonValidation>;

const StringListValidation = BlankValidation(FeatureType.StringList);
export type StringListValidation = z.infer<typeof StringListValidation>;

export const FeatureValidationSchema = z.discriminatedUnion('type', [
  BooleanValidation,
  StringValidation,
  NumberValidation,
  MarkdownValidation,
  MultiSelectValidation,
  JsonValidation,
  StringListValidation,
]);
export type FeatureValidation = z.infer<typeof FeatureValidationSchema>;

export const FeatureValidationSchemaMap = {
  [FeatureType.Boolean]: BooleanValidation,
  [FeatureType.String]: StringValidation,
  [FeatureType.Number]: NumberValidation,
  [FeatureType.Markdown]: MarkdownValidation,
  [FeatureType.MultiSelect]: MultiSelectValidation,
  [FeatureType.Json]: JsonValidation,
  [FeatureType.StringList]: StringListValidation,
};

export const FeatureDetailSchema = z
  .object({
    propName: z.string(),
    displayName: z.string(),
    description: z.string(),
    category: z.object({
      id: z.number(),
      name: z.string(),
    }),
    areas: z.array(FeatureAreaSchema),
    source: z
      .object({
        name: z.string(),
      })
      .nullable(),
    feature: FeatureValueSchema,
    validation: FeatureValidationSchema,
  })
  .refine((a) => a.feature.type === a.validation.type, {
    error: 'Feature type and validation type do not match',
  });
export type FeatureDetail = z.infer<typeof FeatureDetailSchema>;

export const homePageSchema = z.enum(['chat', 'workspace', 'analyst-main']);
export type HomePage = z.infer<typeof homePageSchema>;

// ─── JSON feature schemas ─────────────────────────────────────────────────────

export const FormWorkflowItemSchema = z.object({ workflowKey: z.string() });
export type FormWorkflowItem = z.infer<typeof FormWorkflowItemSchema>;
export const FormWorkflowsSchema = z.array(FormWorkflowItemSchema);
export type FormWorkflows = z.infer<typeof FormWorkflowsSchema>;

/**
 * Map of propName -> Zod schema for JSON-typed features. Derived from the
 * registry: every JsonDef contributes its own schema, replacing the legacy
 * hand-maintained map.
 */
export const JsonFeatureSchemaRegistry: Partial<Record<string, z.ZodSchema>> = Object.fromEntries(
  FEATURES.filter(isJsonDef).map((def) => [def.propName, def.schema as z.ZodSchema])
);

// ─── Flat agent features model ────────────────────────────────────────────────

const flatAgentFeaturesDefs = [
  smartRoutingEnabled,
  citationsEnabled,
  formWorkflows,
  availableWidgets,
  additionalAgentContext,
  identificationPrompt,
  inputGuardrailsPrompt,
  viewHcpProfileTool,
  hcpAlertsTool,
  repTwoMinuteSummaryTool,
] as const;

export const flatAgentFeaturesModel = buildFlatFeaturesModel(flatAgentFeaturesDefs);
export type FlatAgentFeatures = z.infer<typeof flatAgentFeaturesModel>;

const flatFeaturesDefs = [
  autoTagFiles,
  workspaces,
  workspaceBrands,
  hcpProfiles,
  chatVoiceInput,
  chatReadAloud,
  chatWelcomeScreen,
  chatLiveAvatar,
  chatLiveAvatarId,
  chatLiveAvatarTtsConfig,
  chatReadAloudTtsConfig,
  chatInteractionAvailableModes,
  chatInteractionDefaultMode,
  smartRouting,
  smartRoutingEnabled,
  voiceMode,
  deleteChatsOnSessionEnd,
  chatAggregationVisualization,
  hcpSchedulingAssistant,
  followUps,
  predictiveScheduling,
  chatGuidelines,
  chatExpirationHours,
  speechServicesProvider,
  uploadFiles,
  copyChatMessages,
  shareChats,
  exportChatData,
  engagementResponseLabels,
  engagementFilter,
  keyDiscussionQuestionsDisclaimer,
  chatIconVisibility,
  supportIcon,
  documentIconVisibility,
  chatAuraIframesEnabled,
  chatFileUploads,
  hcpContactInformation,
  hcpInteractionsSummary,
  hcpKeyDiscussionQuestions,
  hcpKeyInsights,
  hcpLeaderTopics,
  hcpClinicalTrials,
  hcpPublications,
  hcpConferencePresence,
  hcpOpensearchIndex,
  browserTabName,
  complianceLayer,
  maxUserChatQueries,
  disableChatInputWithLandingCards,
  homeScreen,
  homeButton,
  homeButtonTopHeader,
  useNewExperience,
  faqIcon,
  faqChatContextIdentifier,
  chatContextSelector,
  applicationTooltip,
  chatWelcomeScreenContent,
  formWorkflows,
  formWorkflow,
  availableWidgets,
  additionalAgentContext,
  identificationPrompt,
  inputGuardrailsPrompt,
  chatAtMentionFilter,
  chatInputAttachmentsEnabled,
  chatInputAttachmentAllowedMimeTypes,
  repAutocompleteEndpoint,
  repSearchEndpoint,
] as const;

export const flatFeaturesModel = buildFlatFeaturesModel(flatFeaturesDefs);
export type FlatFeatures = z.infer<typeof flatFeaturesModel>;

const ParserMap = {
  [FeatureType.Boolean]: z.enum(['true', 'false']).transform((value) => value === 'true'),
  [FeatureType.String]: z.string(),
  [FeatureType.Number]: z.coerce.number(),
  [FeatureType.Markdown]: z.string(),
  [FeatureType.MultiSelect]: z
    .string()
    .transform((value) => JSON.parse(value) as unknown)
    .pipe(z.array(z.string())),
  [FeatureType.StringList]: z
    .string()
    .transform((value) => JSON.parse(value) as unknown)
    .pipe(z.array(z.string())),
};

type ParseResult =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      value: FeatureValue;
    };

export function parseFeatureValue(value: string, type: FeatureType, propName?: string): ParseResult {
  if (type === FeatureType.Json) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      return { success: false as const, message: `Invalid JSON value for feature` };
    }

    const registeredSchema = propName !== undefined ? JsonFeatureSchemaRegistry[propName] : undefined;
    if (registeredSchema) {
      const result = registeredSchema.safeParse(parsed);
      if (!result.success) {
        return { success: false as const, message: result.error.message };
      }
    }

    return { success: true, value: { type, value: parsed } as FeatureValue };
  }

  const parser = ParserMap[type];

  const result = parser.safeParse(value);
  if (!result.success) {
    return {
      success: false as const,
      message: result.error.message,
    };
  } else {
    return {
      success: true,
      value: {
        value: parser.parse(value),
        type,
      } as FeatureValue,
    };
  }
}

type ValidationParseResult =
  | {
      success: false;
      message: string;
    }
  | {
      success: true;
      value: FeatureValidation;
    };

export function parseFeatureValidations(value: object, type: FeatureType): ValidationParseResult {
  const parser = FeatureValidationSchemaMap[type];

  const result = parser.safeParse({ ...value, type });
  if (!result.success) {
    return {
      success: false as const,
      message: result.error.message,
    };
  } else {
    return {
      success: true,
      value: result.data,
    };
  }
}

export function stringifyFeatureValue(featureValue: FeatureValue): string {
  switch (featureValue.type) {
    case FeatureType.Boolean:
      return featureValue.value ? 'true' : 'false';
    case FeatureType.String:
      return featureValue.value;
    case FeatureType.Number:
      return featureValue.value.toString();
    case FeatureType.Markdown:
      return featureValue.value;
    case FeatureType.MultiSelect:
      return JSON.stringify(featureValue.value);
    case FeatureType.Json:
      return JSON.stringify(featureValue.value);
    case FeatureType.StringList:
      return JSON.stringify(featureValue.value);
    default:
      throw new Error(`Unknown feature type: ${(featureValue as FeatureValue).type}`);
  }
}
